import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import { useState, useEffect, useRef, useCallback } from "react";
import { Table, Form, InputGroup, Button } from "react-bootstrap";
import {
  ITEMS_PER_PAGE,
  SEARCH_DEBOUNCE_TIME,
} from "../../../../constant/constant";
import TeacherTableRow from "./TeacherTableRow";
import "../../../../common/styles/table.css";
import { useGetTeachers } from "../../../../hooks/school/teacher/useGetTeachers";
import { useDeleteTeacher } from "../../../../hooks/school/teacher/useDeleteTeacher";
import TableError from "../../common/TableError";
import TableSkeleton from "../../common/TableSkeleton";
import { useLocation } from "react-router-dom";
import RefreshButtonTable from "../../../common/RefreshButtonTable";
import RoleGuard from "../../../auth/RoleGuard";

const TeacherTable = () => {
  const { loading, error, teachers, getTeachers, metaData } = useGetTeachers();
  const {
    deleteTeacher,
    loading: deleteLoading,
    error: deleteError,
    success: deleteSuccess,
    resetState: resetDeleteState,
  } = useDeleteTeacher();
  const [currentPage, setCurrentPage] = useState(1);
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimeoutRef = useRef(null);

  /**
   * Fetch teachers with current page and search parameter
   */
  const fetchTeachers = useCallback(
    (page, search) => {
      // getTeachers signature: (page, per_page, append, search)
      getTeachers(page, ITEMS_PER_PAGE, false, search.trim());
    },
    [getTeachers],
  );

  // Fetch teachers on initial load, when page changes, or when search changes
  useEffect(() => {
    fetchTeachers(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch, location.search]);

  /**
   * Handle search input change with debounce
   */
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      setDebouncedSearch(value);
    }, SEARCH_DEBOUNCE_TIME);
  };

  /**
   * Clear search input and reset results
   */
  const handleClearSearch = () => {
    setSearchTerm("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setCurrentPage(1);
    setDebouncedSearch("");
  };

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    fetchTeachers(currentPage, debouncedSearch);
  };

  /**
   * Handle teacher deletion
   * @param {string} teacherId - The ID of the teacher to delete
   */
  const handleDeleteTeacher = async (teacherId) => {
    await deleteTeacher(teacherId);
  };

  /**
   * Handle successful deletion - refresh the list
   */
  const handleDeleteSuccess = () => {
    // Go to previous page if we deleted the last item on the current page
    if (teachers && teachers.length === 1 && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    } else {
      handleRefresh();
    }
  };

  // Show skeleton only on initial load (when teachers is undefined)
  if (loading && teachers === undefined) {
    return <TableSkeleton rows={ITEMS_PER_PAGE} columns={6} />;
  }

  if (error) {
    return (
      <TableError
        error={error}
        handleRefresh={handleRefresh}
        loading={loading}
      />
    );
  }

  const totalPages = metaData?.last_page || 1;
  const hasTeachers = teachers && teachers.length > 0;
  const isSearching = debouncedSearch.trim() !== "";

  return (
    <div>
      {/* Search and Refresh Row */}
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <InputGroup style={{ maxWidth: "400px" }}>
          <InputGroup.Text>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={loading && !isSearching && teachers === undefined}
            className="shadow-none"
          />
          {searchTerm && (
            <Button
              variant="outline-secondary"
              onClick={handleClearSearch}
              disabled={loading}
              title="Clear search"
            >
              <i className="bi bi-x-lg"></i>
            </Button>
          )}
        </InputGroup>

        <RefreshButtonTable handleRefresh={handleRefresh} loading={loading} />
      </div>

      {!hasTeachers ? (
        <div className="alert alert-info mt-3" role="alert">
          {isSearching
            ? `No teachers found matching "${debouncedSearch}".`
            : "No teachers found."}
        </div>
      ) : (
        <div className="custom-table-container mt-3">
          <div className="custom-table-wrapper">
            <Table hover className="custom-table">
              <thead className="custom-table-head">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Assigned Students</th>
                  <th>Status</th>
                  <RoleGuard permission="teachers_classrooms">
                    <th>Classrooms</th>
                  </RoleGuard>
                  <RoleGuard permission="teachers_edit_delete">
                    <th>Action</th>
                  </RoleGuard>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => {
                  return (
                    <TeacherTableRow
                      key={teacher.id}
                      teacher={teacher}
                      onDelete={handleDeleteTeacher}
                      deleteLoading={deleteLoading}
                      deleteError={deleteError}
                      deleteSuccess={deleteSuccess}
                      resetDeleteState={resetDeleteState}
                      onDeleteSuccess={handleDeleteSuccess}
                      RoleGuard={RoleGuard}
                    />
                  );
                })}
              </tbody>
            </Table>
          </div>

          <TablePagination
            totalPage={totalPages}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default TeacherTable;
